import axios from "axios";

const API_URL = `${import.meta.env.VITE_API_URL}/products`;

// Types
export interface ProductCategory {
  _id: string;
  name: string;
}

export interface ProductBranch {
  _id: string;
  name: string;
}

export interface Product {
  _id: string;
  name: string;
  category: ProductCategory;
  branch: ProductBranch;
  bunch?: number;
  image?: string;
  colorCode?: string;
  colorName?: string;
  variants: string[];
}

export interface ProductsResponse {
  success: boolean;
  products: Product[];
  categories: { _id: string; name: string }[];
  page: number;
  pages: number;
  search: string;
  category: string;
  branch: string;
  sortBy: string;
  sortOrder: string;
}

export interface FetchProductsOptions {
  branchId: string;
  categoryId?: string;
  page?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: string;
}

// API functions
export const fetchProductsByBranch = async (
  options: FetchProductsOptions
): Promise<ProductsResponse> => {
  const {
    branchId,
    categoryId,
    page = 1,
    search = "",
    sortBy = "name",
    sortOrder = "asc",
  } = options;

  const params = new URLSearchParams({
    page: String(page),
    search,
    sortBy,
    sortOrder,
  });

  if (categoryId) {
    params.append("category", categoryId);
  }

  const { data } = await axios.get<ProductsResponse>(
    `${API_URL}/branch/${branchId}?${params.toString()}`
  );
  return data;
};

export interface PrivateProductsResponse {
  success: boolean;
  products: Product[];
  page: number;
  pages: number;
  search: string;
  branch: string;
  sortBy: string;
  sortOrder: string;
}

export interface FetchPrivateProductsOptions {
  branchId: string;
  page?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: string;
}

export const fetchPrivateProducts = async (
  options: FetchPrivateProductsOptions
): Promise<PrivateProductsResponse> => {
  const { branchId, page = 1, search = "", sortBy = "name", sortOrder = "asc" } = options;
  const params = new URLSearchParams({ page: String(page), search, sortBy, sortOrder });
  const { data } = await axios.get<PrivateProductsResponse>(
    `${API_URL}/branch/${branchId}/private?${params.toString()}`
  );
  return data;
};

export const makeProductPublic = async (
  productId: string
): Promise<{ success: boolean; message: string }> => {
  const { data } = await axios.put<{ success: boolean; message: string }>(
    `${API_URL}/${productId}/make-public`
  );
  return data;
};

// Fetch all products for a category (single request, no pagination)
export const fetchAllProductsForCategory = async (
  branchId: string,
  categoryId: string
): Promise<Product[]> => {
  const { data } = await axios.get<{ success: boolean; products: Product[]; count: number }>(
    `${API_URL}/branch/${branchId}/category/${categoryId}/all`
  );
  return data.products;
};
